import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, Target, Globe, Smartphone, TrendingUp, Award } from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - CollabOrb | Connecting Brands with creators</title>
        <meta name="description" content="CollabOrb is a tech platform that connects brands with creators across India to run marketing campaigns easily and transparently." />
        <meta name="keywords" content="creator marketing, brand collaboration, India, marketing campaigns, social media" />
      </Helmet>

      <Navbar />
      
      <div className="site-shell min-h-screen pt-20">
        {/* Hero Section */}
        <div className="px-4">
          <div className="max-w-6xl mx-auto rounded-[2rem] bg-collaborb-inner text-white py-16 px-6 sm:px-10 text-center shadow-2xl">
            <div className="section-kicker mb-4 !bg-white/10 !text-sky-200">About CollabOrb</div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Built to simplify creator partnerships.</h1>
            <p className="text-xl sm:text-2xl max-w-4xl mx-auto leading-relaxed text-slate-300">
              A tech platform that connects brands with creators across India to run marketing campaigns easily and transparently.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          
          {/* Mission Statement */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We're democratizing creator marketing by making it accessible, transparent, and effective for businesses of all sizes while empowering creators to monetize their content and grow their audience.
            </p>
          </div>

          {/* For Brands Section */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="app-card rounded-[1.75rem] p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">For Brands</h3>
              </div>
              
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">D2C Companies & Startups</h4>
                  <p className="text-gray-600">Small startups, fashion/beauty/FMCG brands, and tech startups looking to build brand awareness and drive sales through authentic creator partnerships.</p>
                </div>
                
                <div className="border-l-4 border-collab-accent pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Marketing Agencies</h4>
                  <p className="text-gray-600">Agencies managing campaigns for multiple clients, streamlining creator discovery, campaign management, and performance tracking.</p>
                </div>
                
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Regional Businesses</h4>
                  <p className="text-gray-600">Vernacular product businesses targeting audiences in Hindi, Tamil, Marathi, and other regional languages across India.</p>
                </div>
              </div>
            </div>

            {/* For Creators Section */}
            <div className="app-card rounded-[1.75rem] p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">For Creators</h3>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Nano & Micro Creators</h4>
                  <p className="text-gray-600">Creators with 2K–10K followers (Nano) and 10K–100K followers (Micro) who want to monetize their engaged communities.</p>
                </div>
                
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Multi-Platform Creators</h4>
                  <p className="text-gray-600">Content creators on YouTube, Instagram, Moj, Josh, ShareChat, and other social media platforms.</p>
                </div>
                
                <div className="border-l-4 border-indigo-500 pl-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Regional Content Creators</h4>
                  <p className="text-gray-600">Creators producing content in regional languages, helping brands connect with diverse Indian audiences.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Features */}
          <div className="app-card rounded-[1.75rem] p-8 mb-16">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-collab-accent-soft rounded-full mb-4">
                <Globe className="w-8 h-8 text-collab-brand-accent" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Why Choose CollabOrb?</h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Easy to Use</h4>
                <p className="text-gray-600">Simple, intuitive platform that makes campaign creation and management effortless for both brands and creators.</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-collab-accent-soft rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6 text-collab-brand-accent" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Transparent</h4>
                <p className="text-gray-600">Clear pricing, detailed analytics, and transparent communication ensure fair partnerships and measurable results.</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">India-Focused</h4>
                <p className="text-gray-600">Built specifically for the Indian market, supporting regional languages and understanding local culture.</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="rounded-[1.75rem] bg-gradient-to-r from-collaborb-inner via-collaborb-spherical-mid to-collaborb-band4 text-white p-8 text-center shadow-2xl">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your Journey?</h3>
            <p className="text-lg mb-6 opacity-90">
              Join thousands of brands and creators who are already creating successful campaigns on CollabOrb.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center">
                Start as a Brand
              </Link>
              <Link to="/signup" className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors text-center">
                Join as creator
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AboutPage;
